import React from 'react'
import ReactDOM from 'react-dom'
import App from './app'
import axios from 'axios'
import { createHttpLink, ApolloClient, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { ApolloProvider } from '@apollo/client'
import moment from 'moment'
import { Translations } from './translations'
import 'moment/locale/fi'
import { ConfigProvider } from 'antd'
import fi_FI from 'antd/lib/locale/fi_FI'
import { CurrentUserProvider } from 'context/current-user'

import 'antd/dist/antd.css'
import { EmployeeReportingProvider } from 'context/employee-reporting'
import { CustomersProvider } from 'context/customers'
import { EmployeesProvider } from 'context/employees'

import { onError } from 'apollo-link-error'
import 'primereact/resources/themes/lara-light-indigo/theme.css' //theme
import 'primereact/resources/primereact.min.css' //core css
import 'primeicons/primeicons.css'
import { addLocale, locale } from 'primereact/api'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { fiFI } from '@mui/material/locale'
import CookieConsentPrompt from 'components/cookie-consent'

moment.locale('fi')

type Config = {
  backendUri: string
}

addLocale('fi', {
  firstDayOfWeek: 1,
  dayNames: ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'],
  dayNamesShort: ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'],
  dayNamesMin: ['SU', 'MA', 'TI', 'KE', 'TO', 'PE', 'LA'],
  monthNames: [
    'Tammikuu',
    'Helmikuu',
    'Maaliskuu',
    'Huhtikuu',
    'Toukokuu',
    'Kesäkuu',
    'Heinäkuu',
    'Elokuu',
    'Syyskuu',
    'Lokakuu',
    'Marraskuu',
    'Joulukuu'
  ],
  monthNamesShort: ['Tammi', 'Helmi', 'Maalis', 'Huhti', 'Touku', 'Kesä', 'Heinä', 'Elo', 'Syys', 'Loka', 'Marras', 'Joulu'],
  today: 'Tänään',
  clear: 'Tyhjennä'
})
locale('fi')

declare global {
  namespace NodeJS {
    interface Global {
      Config: Config
      Apollo: ApolloClient<any>
      Translations: Translations
    }
  }
}

const start = async () => {
  global.Translations = new Translations('fi')

  try {
    // Configs

    const { data } = await axios.get('/config.json')

    global.Config = data

    // Axios

    axios.interceptors.request.use((config: any) => {
      const token = localStorage.getItem('token') || undefined
      config.headers.Authorization = token
      return config
    })

    // GraphQL

    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
        )
      if (networkError) console.log(`[Network error]: ${networkError}`)
    })

    const httpLink = createHttpLink({
      uri: global.Config.backendUri + '/graphql'
    })

    const authLink = setContext((_, { headers }) => {
      const token = localStorage.getItem('token')
      const city = localStorage.getItem('locationSelector')
      return {
        headers: {
          ...headers,
          authorization: token ? `${token}` : '',
          'x-city-code': city ? `${city}` : ''
        }
      }
    })

    global.Apollo = new ApolloClient({
      link: authLink.concat(httpLink),
      cache: new InMemoryCache({
        addTypename: false
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only'
        }
      }
    })

    const theme = createTheme(
      {
        palette: {
          primary: { main: '#1976d2' }
        }
      },
      fiFI
    )

    const Root = () => {
      return (
        <ApolloProvider client={global.Apollo}>
          <CurrentUserProvider>
            <EmployeesProvider>
              <CustomersProvider>
                <EmployeeReportingProvider>
                  <ConfigProvider locale={fi_FI}>
                    <ThemeProvider theme={theme}>
                      <div className="c-app">
                        <App />
                        <CookieConsentPrompt />
                      </div>
                    </ThemeProvider>
                  </ConfigProvider>
                </EmployeeReportingProvider>
              </CustomersProvider>
            </EmployeesProvider>
          </CurrentUserProvider>
        </ApolloProvider>
      )
    }

    ReactDOM.render(<Root />, document.getElementById('root'))
  } catch (e) {
    throw e
  }
}

start()
